import { ThemeEvents } from '@theme/events';

/**
 * B&S Parfums design wiring (Zone A).
 * Replaces the design prototype script: every behavior it faked is wired here
 * to the host theme's real mechanisms (cart drawer, CartUpdateEvent,
 * VariantUpdateEvent) or implemented standalone (mobile menu, accordion,
 * gallery thumbs, sticky ATC reveal).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function on(selector, handler) {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest(selector);
    if (target) handler(target, event);
  });
}

/* --- Cart: open host drawer from the design's pill button --- */
on('[data-cart-toggle]', (_, event) => {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open();
  }
});

/* --- Cart: live count + total in the design header --- */
async function refreshCartSummary(itemCount) {
  if (typeof itemCount === 'number') {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(itemCount);
    });
  }
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
    });
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: cart.currency || 'EUR',
    }).format(cart.total_price / 100);
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = formatted.replace(/^(\D+)\s?/, '€');
    });
  } catch {
    /* header badge falls back to server-rendered values */
  }
}
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  refreshCartSummary(event.detail?.data?.itemCount);
});

/* --- Mobile menu (design's own markup) --- */
function closeMenu() {
  document.querySelector('.m-menu')?.classList.remove('open');
  document.querySelector('.m-menu__ov')?.classList.remove('open');
}
on('[data-menu-toggle]', () => {
  document.querySelector('.m-menu')?.classList.add('open');
  document.querySelector('.m-menu__ov')?.classList.add('open');
});
on('[data-menu-close]', closeMenu);
on('.m-menu__ov', closeMenu);

/* --- Accordion (design markup, max-height transition) --- */
on('.acc__head', (head) => {
  const item = head.closest('.acc__item');
  const body = item?.querySelector('.acc__body');
  if (!item || !body) return;
  if (item.classList.contains('open')) {
    item.classList.remove('open');
    body.style.maxHeight = '0px';
  } else {
    item.classList.add('open');
    body.style.maxHeight = `${body.scrollHeight}px`;
  }
});
document.querySelectorAll('.acc__item.open .acc__body').forEach((body) => {
  body.style.maxHeight = `${body.scrollHeight}px`;
});

/* --- PDP gallery: thumb select + per-variant filtering --- */
function bsSelectMedia(gallery, thumb) {
  const mainImg = gallery.querySelector('.pdp__main img');
  if (!mainImg || !thumb) return;
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => t.classList.remove('on'));
  thumb.classList.add('on');
  // Swap the main image to the HIGH-RES source (data-full = 2000px). Never copy
  // the 400px thumbnail src into the main — that was the "low res" bug.
  const full = thumb.dataset.full;
  if (full) {
    mainImg.removeAttribute('srcset');
    mainImg.removeAttribute('sizes');
    mainImg.src = full;
  }
}
on('.pdp__thumb', (thumb) => {
  const gallery = thumb.closest('.pdp__gallery');
  if (gallery) bsSelectMedia(gallery, thumb);
});

/* Show only the selected variant's media (+ media assigned to no variant);
   swap the main image to the variant's featured media. */
function bsFilterGallery(variant) {
  const gallery = document.querySelector('.pdp__gallery');
  if (!gallery || !variant) return;
  const curId = String(variant.id);
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => {
    const ids = (t.dataset.variantIds || '').trim();
    t.hidden = ids !== '' && !ids.split(/\s+/).includes(curId);
  });
  const fmId = variant.featured_media && String(variant.featured_media.id);
  let target = fmId && gallery.querySelector(`.pdp__thumb[data-media-id="${fmId}"]`);
  if (!target) target = gallery.querySelector('.pdp__thumb:not([hidden])');
  if (target) bsSelectMedia(gallery, target);
}

/* --- Sticky ATC: reveal when the buy box leaves the viewport --- */
const buyBox = document.querySelector('.pdp__buy');
const stickyBar = document.querySelector('.pdp-sticky');
if (buyBox && stickyBar) {
  const update = () => {
    const rect = buyBox.getBoundingClientRect();
    stickyBar.classList.toggle('show', rect.bottom < 80);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
  // Sticky ATC submits the main product form
  stickyBar.querySelector('[data-bs-sticky-atc]')?.addEventListener('click', (event) => {
    event.preventDefault();
    if (event.currentTarget.disabled) return;
    const form = document.querySelector('.pdp__buybox product-form-component form');
    if (form) form.requestSubmit();
  });
}

/* --- PDP performance bars: same data source as the old theme's
       _fragrance-performance block (external scores API). --- */
const ratings = document.querySelector('[data-bs-ratings]');
if (ratings) {
  const fmt = (v) => v.toFixed(1).replace('.', ',');
  fetch(`https://api.parfumproben-bestellen.de/data-bs?id=${ratings.dataset.productId}`)
    .then((res) => {
      if (!res.ok) throw new Error('no performance data');
      return res.json();
    })
    .then((data) => {
      const metrics = { overall: data.score, longevity: data.longevity, sillage: data.sillage };
      let any = false;
      for (const [key, raw] of Object.entries(metrics)) {
        const metric = ratings.querySelector(`[data-metric="${key}"]`);
        const value = parseFloat(raw);
        if (!metric) continue;
        if (!raw || Number.isNaN(value)) {
          metric.remove();
          continue;
        }
        any = true;
        const bar = metric.querySelector('[data-bar-fill]');
        const display = metric.querySelector('[data-value-display]');
        if (display) display.textContent = fmt(value);
        if (bar) {
          if (!REDUCED_MOTION) bar.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
          requestAnimationFrame(() => {
            ratings.hidden = false;
            requestAnimationFrame(() => {
              bar.style.width = `${(value / 10) * 100}%`;
            });
          });
        }
      }
      if (!any) ratings.remove();
    })
    .catch(() => ratings.remove());
}

/* --- Variant change: filter the gallery + mirror the live price into the sticky bar --- */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = event.detail?.resource;
  bsFilterGallery(variant);
  // Mirror availability into the sticky ATC. The inline buy button is handled
  // server-side + by product-form.js, but the floating sticky mirror has no
  // such wiring — without this it stays clickable on a sold-out variant.
  const stickyAtc = document.querySelector('.pdp-sticky [data-bs-sticky-atc]');
  if (stickyAtc && variant) {
    const available = variant.available !== false;
    stickyAtc.disabled = !available;
    stickyAtc.classList.toggle('is-disabled', !available);
    stickyAtc.textContent = available ? 'In den Warenkorb' : 'Ausverkauft';
  }
  // product-price replaces the main price container after this event; copy on next tick.
  setTimeout(() => {
    const main = document.querySelector('.pdp__price');
    const sticky = document.querySelector('.pdp-sticky__price');
    if (main && sticky) sticky.textContent = main.textContent.trim();
  }, 0);
});

/* --- Reduced-motion: disable max-height animation --- */
if (REDUCED_MOTION) {
  document.querySelectorAll('.acc__body').forEach((body) => {
    body.style.transition = 'none';
  });
}

/* --- Announcement bar: rotate messages every 5s + working prev/next arrows
       (replaces the old theme's multi-message bar; texts are section settings). --- */
document.querySelectorAll('[data-announce-rotator]').forEach((rotator) => {
  const msgs = [...rotator.querySelectorAll('.hdrB__announce-msg')];
  if (msgs.length < 2) return;
  let idx = msgs.findIndex((m) => m.classList.contains('is-active'));
  if (idx < 0) idx = 0;
  let timer;
  const show = (n) => {
    msgs[idx].classList.remove('is-active');
    idx = (n + msgs.length) % msgs.length;
    msgs[idx].classList.add('is-active');
  };
  const start = () => {
    timer = setInterval(() => show(idx + 1), 5000);
  };
  const reset = () => {
    clearInterval(timer);
    start();
  };
  rotator.querySelector('[data-announce-prev]')?.addEventListener('click', () => {
    show(idx - 1);
    reset();
  });
  rotator.querySelector('[data-announce-next]')?.addEventListener('click', () => {
    show(idx + 1);
    reset();
  });
  start();
});
